/*
 * Cadwork wEngine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @file Math functions library.
 *
 * @author Tomáš Burian (burian@cadwork.cz)
 */

export const FLOAT_PRECISION: number = 0.000001;
export const EPSILON: number = Math.pow(2, -52);

export function normalizeAngle(aAngle: number): number {
    return (aAngle + 360) % 360;
}

export function degToRad(aAngle: number): number {
    return (aAngle * Math.PI) / 180.0;
}

export function radToDeg(aAngle: number): number {
    return (aAngle * 180.0) / Math.PI;
}

export function hexToRgb(aHexValue: string, aZeroToOne: boolean): {r: number, g: number, b: number} | null {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(aHexValue);
    let frac = aZeroToOne ? 255.0 : 1;
    return result ? {
        r: parseInt(result[1], 16) / frac,
        g: parseInt(result[2], 16) / frac,
        b: parseInt(result[3], 16) / frac
    } : null;
}

export function componentToHex(c: number): string {
    let hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
}

export function rgbToHex(r: number, g: number, b: number): string {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

/**
 * Converts CSS color string to RGB color value.
 *
 * @param {string} aCssColorString CSS property, for example rgb(64, 64, 64).
 * @param {boolean} aZeroToOne      Convert result values into <0, 1> interval if true, otherwise <0, 255>.
 * @returns {number[]}       Array of [R, G, B] result values.
 *
 */
export function cssColorToRgb(aCssColorString: string, aZeroToOne: boolean): number[] {
    let rgb: RegExpMatchArray | null = aCssColorString.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (!rgb) {
        return [0.0, 0.0, 0.0];
    }
    let frac: number = aZeroToOne ? 255.0 : 1.0;
    return [+rgb[1] / frac, +rgb[2] / frac, +rgb[3] / frac];
}

/**
 * PHP like rounding with a precision parameter.
 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round
 *
 * Examples:
 * cadwork.Math.round(1234.5678, 1); // 1234.6
 * cadwork.Math.round(1234.5678, -1); // 1230
 *
 * @param {number} aNumber
 * @param {number} precision
 * @returns {number}
 */
export function round(aNumber: number, precision: number): number {
    if (precision === undefined) {
        return Math.round(aNumber);
    }
    let factor = Math.pow(10, precision);
    let tempNumber = aNumber * factor;
    let roundedTempNumber = Math.round(tempNumber);
    return roundedTempNumber / factor;
}

/**
 * Round number (17.5, 5) -> 20
 *
 * @param {number} aNumber
 * @param {number} step
 * @returns {number}
 */
export function roundToStep(aNumber: number, step: number): number {
    let tmp = aNumber % step;
    let a = tmp / step;
    if (a >= 0.5) {
        return Math.ceil(aNumber / step) * step;
    } else {
        return Math.floor(aNumber / step) * step;
    }
}

/**
 * Test for almost zero in all components
 *
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @return {boolean}
 */
export function isZero3(x: number, y: number, z: number): boolean {
    if (Math.abs(x) >= EPSILON) {
        return false;
    } else if (Math.abs(y) >= EPSILON) {
        return false;
    } else if (Math.abs(z) >= EPSILON) {
        return false;
    }
    return true;
}

/**
 * Test for almost the same values
 *
 * @param {number} a
 * @param {number} x
 * @return {boolean}
 */
export function equal(a: number, x: number): boolean {
    return Math.abs(a - x) < EPSILON;
}
